import React, {
  useCallback,
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import { FormattedMessage } from 'react-intl';
import {
  keyBy,
  uniq,
  flatten,
} from 'lodash';

import { stripesConnect } from '@folio/stripes/core';
import { LoadingPane } from '@folio/stripes/components';

import {
  EntityNotFound,
} from '../components';
import JobTemplateForm from '../../components/job-template/job-template-form';
import { useCallout } from '../../hooks';
import {
  getCreateEditJobTemplatePayload,
  getFirstLastUserName,
  removeEmptyLinkage,
} from '../../utils';
import {
  FULL_PANE_WIDTH,
  UPDATE_STATUS,
  TASK_TEMPLATES_FIELDS,
  JOB_TEMPLATE_FIELDS,
  JOB_TEMPLATE_INITIAL_VALUES,
  TASK_ASSIGN_TO_FIELDS,
  TASK_TEMPLATES_INITIAL_VALUES,
  LOAD_LIMIT,
  getJobTemplatesViewUrl,
} from '../../constants';
import { IfPermissionWrapper } from '../../components/common';

const JobTemplateEditRoute = ({
  mutator,
  history,
  location,
}) => {
  const [jobTemplate, setJobTemplate] = useState({});

  const [isLoaded, setIsLoaded] = useState(false);
  const [isFailed, setIsFailed] = useState(false);

  const formatInitialValues = (jobTemplateFieldValues) => {
    const taskTemplates = jobTemplateFieldValues[JOB_TEMPLATE_FIELDS.TASK_TEMPLATES].map((taskTemplate) => ({
      ...taskTemplate,
      [TASK_TEMPLATES_FIELDS.ASSIGN_TO]: taskTemplate[TASK_TEMPLATES_FIELDS.TEAM][TASK_TEMPLATES_FIELDS.ID]
        ? TASK_TEMPLATES_FIELDS.TEAM
        : taskTemplate[TASK_TEMPLATES_FIELDS.ASSIGNED_USER_ID]
          ? TASK_ASSIGN_TO_FIELDS.USER
          : TASK_TEMPLATES_INITIAL_VALUES[TASK_TEMPLATES_FIELDS.ASSIGN_TO],
      [TASK_TEMPLATES_FIELDS.UPDATE_STATUS]: UPDATE_STATUS.UPDATE,
    }));

    return {
      [JOB_TEMPLATE_FIELDS.ID]: jobTemplateFieldValues[JOB_TEMPLATE_FIELDS.ID],
      [JOB_TEMPLATE_FIELDS.NAME]: jobTemplateFieldValues[JOB_TEMPLATE_FIELDS.NAME],
      [JOB_TEMPLATE_FIELDS.DESCRIPTION]: jobTemplateFieldValues[JOB_TEMPLATE_FIELDS.DESCRIPTION] || JOB_TEMPLATE_INITIAL_VALUES.DESCRIPTION,
      [JOB_TEMPLATE_FIELDS.TASK_TEMPLATES]: taskTemplates,
      [JOB_TEMPLATE_FIELDS.METADATA]: jobTemplateFieldValues[JOB_TEMPLATE_FIELDS.METADATA],
      [JOB_TEMPLATE_FIELDS.CHANGE_VERSION]: jobTemplateFieldValues[JOB_TEMPLATE_FIELDS.CHANGE_VERSION],
      [JOB_TEMPLATE_FIELDS.RECORD_LINKAGE]: jobTemplateFieldValues[JOB_TEMPLATE_FIELDS.RECORD_LINKAGE]
        ? jobTemplateFieldValues[JOB_TEMPLATE_FIELDS.RECORD_LINKAGE]
        : JOB_TEMPLATE_INITIAL_VALUES[JOB_TEMPLATE_FIELDS.RECORD_LINKAGE],
    };
  };

  useEffect(
    () => {
      let isSubscribed = true;

      mutator.editJobTemplate.GET()
        .then((jobTemplateResponse) => {
          const usersOnlyAssignmentMap = {};

          jobTemplateResponse.taskTemplates.forEach((taskTemplate) => {
            if (!taskTemplate[TASK_TEMPLATES_FIELDS.TEAM][TASK_TEMPLATES_FIELDS.ID] && taskTemplate[TASK_TEMPLATES_FIELDS.ASSIGNED_USER_ID]) {
              usersOnlyAssignmentMap[taskTemplate.id] = taskTemplate[TASK_TEMPLATES_FIELDS.ASSIGNED_USER_ID];
            }
          });
          const userIds = uniq(Object.values(usersOnlyAssignmentMap));

          if (!userIds.length) {
            return jobTemplateResponse;
          }
          const usersPromises = [];

          while (userIds.length) {
            usersPromises.push(mutator.users.GET({
              params: {
                limit: LOAD_LIMIT,
                query: userIds.splice(0, LOAD_LIMIT)
                  .map(userId => `id==${userId}`)
                  .join(' or '),
              }
            }));
          }

          return Promise.all(usersPromises)
            .then((userArrays) => {
              const combinedUsers = flatten(userArrays.map((arr) => arr.users));
              const usersMap = keyBy(combinedUsers, TASK_TEMPLATES_FIELDS.ID);
              const taskTemplatesWithUserNames = jobTemplateResponse.taskTemplates.map((taskTemplate) => {
                if (!taskTemplate[TASK_TEMPLATES_FIELDS.TEAM][TASK_TEMPLATES_FIELDS.ID] &&
                  taskTemplate[TASK_TEMPLATES_FIELDS.ASSIGNED_USER_ID]) {
                  return {
                    ...taskTemplate,
                    [TASK_TEMPLATES_FIELDS.ASSIGNED_USER_NAME]: getFirstLastUserName(usersMap[usersOnlyAssignmentMap[taskTemplate.id]])
                  };
                }

                return taskTemplate;
              });

              jobTemplateResponse.taskTemplates = taskTemplatesWithUserNames;

              return jobTemplateResponse;
            });
        })
        .catch(() => {
          if (isSubscribed) {
            setIsFailed(true);
          }
        })
        .then((jobTemplateResponse) => {
          if (isSubscribed) {
            setJobTemplate(formatInitialValues(jobTemplateResponse));
          }
        })
        .then(() => {
          if (isSubscribed) {
            setIsLoaded(true);
          }
        });

      return () => {
        isSubscribed = false;
      };
    },
    [],
  );

  const showCallout = useCallout();

  const onBack = useCallback(() => {
    history.push({
      pathname: getJobTemplatesViewUrl(jobTemplate[JOB_TEMPLATE_FIELDS.ID]),
      search: location.search,
    });
  }, [history, location.search, jobTemplate[JOB_TEMPLATE_FIELDS.ID]]);

  const onSubmit = useCallback((values) => {
    const requestBody = removeEmptyLinkage(getCreateEditJobTemplatePayload(values));

    mutator.editJobTemplate.PUT({ ...requestBody })
      .then((response) => {
        showCallout({
          message: <FormattedMessage
            id="ui-task-list.callout.updated.jobTemplate"
            values={{ name: response[JOB_TEMPLATE_FIELDS.NAME] }}
          />,
        });
        history.push({
          pathname: getJobTemplatesViewUrl(response[JOB_TEMPLATE_FIELDS.ID]),
          search: location.search,
        });
      })
      .catch(() => {
        showCallout({
          type: 'error',
          message: <FormattedMessage id="ui-task-list.validation.communicationProblem" />,
        });
      });
  }, [jobTemplate[JOB_TEMPLATE_FIELDS.ID], history, location.search, mutator.editJobTemplate, showCallout]);

  if (isFailed) {
    return (
      <EntityNotFound
        errorTextTranslationKey="ui-task-list.error.notFound.jobTemplate"
        pageTitleTranslationKey="ui-task-list.title.edit.notFound"
        onBack={onBack}
      />
    );
  }

  if (!isLoaded) {
    return <LoadingPane defaultWidth={FULL_PANE_WIDTH} />;
  }

  const getTitle = () => (
    <FormattedMessage
      id="ui-task-list.title.edit.jobTemplate"
      values={{ name: jobTemplate[JOB_TEMPLATE_FIELDS.NAME] }}
    />
  );

  return (
    <IfPermissionWrapper perm="ui-task-list.job-template.create-edit">
      <JobTemplateForm
        formTitle={getTitle}
        initialValues={jobTemplate}
        metadata={jobTemplate.metadata}
        onBack={onBack}
        onSubmit={onSubmit}
      />
    </IfPermissionWrapper>
  );
};

JobTemplateEditRoute.manifest = Object.freeze({
  editJobTemplate: {
    type: 'okapi',
    path: 'tasks/job-templates/:{id}',
    clientGeneratePk: false,
    throwErrors: false,
    PUT: { path: 'tasks/job-templates/:{id}' },
    accumulate: true,
    fetch: false,
  },
  users: {
    type: 'okapi',
    path: 'users',
    clientGeneratePk: false,
    throwErrors: false,
    accumulate: true,
    fetch: false,
  },
});

JobTemplateEditRoute.propTypes = {
  history: ReactRouterPropTypes.history.isRequired,
  location: ReactRouterPropTypes.location.isRequired,
  mutator: PropTypes.shape({
    editJobTemplate: PropTypes.shape({
      GET: PropTypes.func.isRequired,
      PUT: PropTypes.func.isRequired,
    }),
    users: PropTypes.shape({
      GET: PropTypes.func.isRequired,
    }),
  }),
};

export default stripesConnect(JobTemplateEditRoute);
